/* ===========================================================================
   Cart drawer upsell — "Frequently bought together"

   The drawer body is replaced wholesale (cartBody.innerHTML = ...) on every
   cart mutation, so this module:
     - binds nothing to the rendered nodes directly (all clicks are delegated
       from document),
     - re-hydrates via MutationObserver whenever a new [data-cd-upsell] lands,
     - caches recommendation payloads per seed product so a qty tweak does not
       re-hit the network.
   =========================================================================== */
(function () {
  'use strict';

  if (window.__cdUpsellInit) return;
  window.__cdUpsellInit = true;

  var CACHE = {}; // seedId+limit -> array of products
  var inflight = {}; // dedupe concurrent fetches for the same key

  /* Shops using the "with currency in HTML" money format ship wrapper markup
     in the format string (e.g. `<span class=money>${{amount}}</span>`). Prices
     are written with textContent, so strip tags or the markup renders as text. */
  function stripTags(str) {
    return String(str).replace(/<[^>]*>/g, '').trim();
  }

  /* theme-variables.liquid declares `moneyFormat` / `cartAddUrl` as top-level
     consts — global scope, but not properties of window — so read them through
     typeof guards rather than off window. */
  function themeMoneyFormat() {
    var fmt = '';
    if (typeof moneyFormat === 'string' && moneyFormat) {
      fmt = moneyFormat;
    } else if (window.theme && window.theme.moneyFormat) {
      fmt = window.theme.moneyFormat;
    }
    fmt = stripTags(fmt);
    return fmt || '${{amount}}';
  }

  function themeAddUrl() {
    if (typeof cartAddUrl === 'string' && cartAddUrl) return cartAddUrl;
    if (typeof cartAdd === 'string' && cartAdd) return cartAdd + '.js';
    return '/cart/add.js';
  }

  /* ---------------------------------------------------------------------
     Money formatting — mirrors Shopify's money filter for the standard
     format strings so prices match the rest of the drawer.
     --------------------------------------------------------------------- */
  function withDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'number' ? precision : 2;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(number) || number === null) return '0';
    var fixed = (number / 100.0).toFixed(precision);
    var parts = fixed.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format) {
    var fmt = format || themeMoneyFormat();

    /* Prefer the theme's own formatter so prices match the rest of the drawer
       exactly. Recommendation prices are already in the presentment currency,
       so no FX conversion is applied here. */
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(cents, fmt);
      } catch (e) {
        /* fall through to the local implementation below */
      }
    }

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholderRegex);
    if (!match) return fmt;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  function sizedImage(src, size) {
    if (!src) return '';
    // Shopify CDN urls accept a width param; keep it small for the drawer.
    return src.indexOf('?') > -1 ? src + '&width=' + size : src + '?width=' + size;
  }

  /* ---------------------------------------------------------------------
     Data
     --------------------------------------------------------------------- */
  function fetchJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (d) {
        return (d && d.products) || [];
      });
  }

  function fetchRecommendations(root) {
    var seed = root.getAttribute('data-seed-product');
    var limit = parseInt(root.getAttribute('data-limit'), 10) || 6;
    var base = root.getAttribute('data-recommendations-url') || '/recommendations/products';

    if (!seed) return Promise.resolve([]);

    // Ask for extra so the rail still fills after cart items are filtered out.
    var ask = Math.min(limit + 4, 10);
    var key = seed + ':' + ask;

    if (CACHE[key]) return Promise.resolve(CACHE[key]);
    if (inflight[key]) return inflight[key];

    var qs = '.json?product_id=' + encodeURIComponent(seed) + '&limit=' + ask + '&intent=';

    inflight[key] = fetchJson(base + qs + 'complementary')
      .then(function (products) {
        // Complementary comes back empty on stores without curated
        // recommendations — fall back to "related" so the rail still fills.
        if (products.length) return products;
        return fetchJson(base + qs + 'related');
      })
      .then(function (products) {
        CACHE[key] = products;
        delete inflight[key];
        return products;
      })
      .catch(function () {
        delete inflight[key];
        return [];
      });

    return inflight[key];
  }

  /* ---------------------------------------------------------------------
     Render
     --------------------------------------------------------------------- */
  function paintPrice(card, price, compareAt) {
    var priceEl = card.querySelector('[data-cd-price]');
    var compareEl = card.querySelector('[data-cd-compare]');
    if (priceEl) priceEl.textContent = formatMoney(price);
    if (compareEl) {
      if (compareAt && compareAt > price) {
        compareEl.textContent = formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  function buildCard(tpl, product) {
    var variants = (product.variants || []).filter(function (v) {
      return v.available;
    });
    if (!variants.length) return null;

    var node = tpl.content.firstElementChild.cloneNode(true);
    var first = variants[0];

    node.setAttribute('data-cd-product', product.id);

    Array.prototype.forEach.call(node.querySelectorAll('[data-cd-link]'), function (a) {
      a.href = product.url;
    });

    var img = node.querySelector('[data-cd-image]');
    var featured = product.featured_image || (product.images && product.images[0]);
    if (img && featured) {
      img.src = sizedImage(featured, 200);
      img.alt = product.title || '';
    } else if (img) {
      img.remove();
    }

    var title = node.querySelector('[data-cd-title]');
    if (title) title.textContent = product.title;

    /* Variant picker only when there is a real choice to make, so the common
       single-variant case stays a one-tap add. */
    var wrap = node.querySelector('[data-cd-variant-wrap]');
    var select = node.querySelector('[data-cd-variant-select]');
    if (wrap && select) {
      if (variants.length > 1) {
        variants.forEach(function (v) {
          var opt = document.createElement('option');
          opt.value = v.id;
          opt.textContent = v.title;
          opt.setAttribute('data-price', v.price);
          opt.setAttribute('data-compare', v.compare_at_price || '');
          select.appendChild(opt);
        });
        select.setAttribute('aria-label', product.title + ' options');
        wrap.hidden = false;
      } else {
        wrap.remove();
      }
    }

    node.setAttribute('data-cd-variant', first.id);
    paintPrice(node, first.price, first.compare_at_price);

    return node;
  }

  function render(root, products) {
    var track = root.querySelector('[data-cd-upsell-track]');
    var tpl = root.querySelector('[data-cd-upsell-template]');
    if (!track || !tpl) return;

    var limit = parseInt(root.getAttribute('data-limit'), 10) || 6;
    var inCart = (root.getAttribute('data-cart-products') || '').split(',').filter(Boolean);

    var shown = 0;
    var frag = document.createDocumentFragment();

    products.forEach(function (product) {
      if (shown >= limit) return;
      if (inCart.indexOf(String(product.id)) > -1) return;
      var card = buildCard(tpl, product);
      if (!card) return;
      frag.appendChild(card);
      shown += 1;
    });

    track.innerHTML = '';
    track.appendChild(frag);

    if (shown > 0) {
      root.hidden = false;
      root.classList.add('cd-upsell--ready');
      updateNav(root);
    } else {
      root.hidden = true;
    }
  }

  /* ---------------------------------------------------------------------
     Scroll affordances
     --------------------------------------------------------------------- */
  function updateNav(root) {
    var track = root.querySelector('[data-cd-upsell-track]');
    var nav = root.querySelector('[data-cd-upsell-nav]');
    if (!track || !nav) return;

    var overflowing = track.scrollWidth - track.clientWidth > 4;
    nav.hidden = !overflowing;
    if (!overflowing) return;

    var prev = root.querySelector('[data-cd-upsell-prev]');
    var next = root.querySelector('[data-cd-upsell-next]');
    var max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  function scrollTrack(root, dir) {
    var track = root.querySelector('[data-cd-upsell-track]');
    if (!track) return;
    var card = track.querySelector('.cd-upsell__card');
    var step = card ? card.offsetWidth + 10 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  /* ---------------------------------------------------------------------
     Add to cart — reuses the theme's ajax-cart section so the drawer
     refreshes exactly the way the rest of the theme expects.
     --------------------------------------------------------------------- */
  var adding = false;

  function refreshDrawer(sections) {
    var html = sections && sections['ajax-cart'];
    if (!html) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var updated = parsed.querySelector('.shopify-section') || parsed.querySelector('[data-cart-wrapper]');
    if (!updated) return;

    var wrapper = updated.querySelector('[data-cart-wrapper]');
    if (!wrapper && updated.hasAttribute('data-cart-wrapper')) wrapper = updated;
    var count = wrapper ? parseInt(wrapper.getAttribute('data-item-count'), 10) || 0 : 0;

    var cartDrawer = document.querySelector('[data-cart-drawer]');
    var cartBody = document.querySelector('[data-cart-drawer-body]');
    if (!cartBody) return;

    cartBody.innerHTML = updated.innerHTML;

    if (typeof localizeAllFreeShippingBars === 'function') localizeAllFreeShippingBars(cartBody);
    if (typeof cartCountUpdate === 'function') cartCountUpdate(count);
    if (count > 0) {
      if (typeof getQuantityElement === 'function') getQuantityElement(cartDrawer || cartBody);
      if (typeof getCartItemRemoveElements === 'function') getCartItemRemoveElements(cartDrawer || cartBody);
    }
    if (typeof cartNoteDrawerInt === 'function' && cartDrawer) cartNoteDrawerInt(cartDrawer);
    if (typeof cartNoteUpdate === 'function') cartNoteUpdate();
  }

  function addVariant(card) {
    if (adding) return;
    var variantId = card.getAttribute('data-cd-variant');
    if (!variantId) return;

    var button = card.querySelector('[data-cd-add]');
    adding = true;
    card.classList.add('is-adding');
    if (button) button.disabled = true;

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append('sections', 'ajax-cart');

    fetch(themeAddUrl(), {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: body
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        adding = false;
        if (data && data.status) {
          // Sold out / limit reached — surface it on the card, keep the drawer.
          card.classList.remove('is-adding');
          card.classList.add('is-error');
          if (button) button.disabled = false;
          setTimeout(function () {
            card.classList.remove('is-error');
          }, 2500);
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsell',
            productVariantId: variantId,
            cartData: data
          });
        }
        refreshDrawer(data.sections);
      })
      .catch(function () {
        adding = false;
        card.classList.remove('is-adding');
        if (button) button.disabled = false;
      });
  }

  /* ---------------------------------------------------------------------
     Delegated events — survive every innerHTML swap of the drawer body.
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var addBtn = event.target.closest('[data-cd-add]');
    if (addBtn) {
      event.preventDefault();
      var card = addBtn.closest('.cd-upsell__card');
      if (card) addVariant(card);
      return;
    }

    var arrow = event.target.closest('[data-cd-upsell-prev], [data-cd-upsell-next]');
    if (arrow) {
      event.preventDefault();
      var root = arrow.closest('[data-cd-upsell]');
      if (root) scrollTrack(root, arrow.hasAttribute('data-cd-upsell-next') ? 1 : -1);
    }
  });

  document.addEventListener('change', function (event) {
    if (!event.target.closest) return;
    var select = event.target.closest('[data-cd-variant-select]');
    if (!select) return;
    var card = select.closest('.cd-upsell__card');
    if (!card) return;
    var opt = select.options[select.selectedIndex];
    card.setAttribute('data-cd-variant', select.value);
    paintPrice(card, parseInt(opt.getAttribute('data-price'), 10), parseInt(opt.getAttribute('data-compare'), 10));
  });

  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track.hasAttribute || !track.hasAttribute('data-cd-upsell-track')) return;
      var root = track.closest('[data-cd-upsell]');
      if (root) updateNav(root);
    },
    true
  );

  window.addEventListener('resize', function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cd-upsell]'), updateNav);
  });

  /* ---------------------------------------------------------------------
     Hydration
     --------------------------------------------------------------------- */
  function hydrate(root) {
    if (!root || root.getAttribute('data-cd-hydrated') === 'true') return;
    root.setAttribute('data-cd-hydrated', 'true');
    fetchRecommendations(root).then(function (products) {
      render(root, products);
    });
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-cd-upsell]');
    Array.prototype.forEach.call(nodes, hydrate);
  }

  // The drawer body is replaced on every cart change; watch for new instances.
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-cd-upsell]')) {
          hydrate(node);
        } else if (node.querySelector) {
          scan(node);
        }
      }
    }
  });

  function boot() {
    scan(document);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
